import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Play, Clock, Database, CheckCircle, XCircle, 
  Loader, Code, Send, ChevronDown, Lightbulb, Eye, EyeOff
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Editor from '@monaco-editor/react';
import { loginUrlWithRedirect } from '../utils/urlConfig';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CodeArenaProblemSolvePage = () => {
  const { problemId } = useParams();
  const navigate = useNavigate();
  const [problem, setProblem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  
  const [code, setCode] = useState('');
  const [language, setLanguage] = useState('python');
  const [showHints, setShowHints] = useState(false);
  
  const isLoggedIn = !!localStorage.getItem('codearena_token');

  const languageDefaults = {
    python: `# Write your solution here
def solve():
    pass

# Read input and call your function
if __name__ == "__main__":
    solve()
`,
    javascript: `// Write your solution here
function solve() {
    
}

// Read input and call your function
solve();
`,
    cpp: `#include <iostream>
#include <vector>
using namespace std;

int main() {
    // Write your solution here
    
    return 0;
}
`,
    java: `import java.util.*;

public class Solution {
    public static void main(String[] args) {
        // Write your solution here
        Scanner sc = new Scanner(System.in);
        
    }
}
`
  };

  useEffect(() => {
    fetchProblem();
  }, [problemId]);

  useEffect(() => {
    setCode(languageDefaults[language]);
  }, [language]);

  const fetchProblem = async () => {
    try {
      const res = await axios.get(`${API}/codearena/problems/${problemId}`);
      setProblem(res.data);
    } catch (err) {
      console.error('Failed to fetch problem:', err);
      toast.error('Problem not found');
      navigate('/codearena/problems');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      toast.error('Please login to submit solutions');
      navigate(loginUrlWithRedirect('/codearena/auth'));
      return;
    }

    if (!code.trim()) {
      toast.error('Please write some code first');
      return;
    }

    setSubmitting(true);
    setResult(null);

    try {
      const token = localStorage.getItem('codearena_token');
      const res = await axios.post(
        `${API}/codearena/submit`,
        {
          problem_id: problemId,
          code: code,
          language: language
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setResult(res.data);
      
      if (res.data.verdict === 'Accepted') {
        toast.success(`Accepted! +${res.data.score_earned} points`);
      } else {
        toast.error(`${res.data.verdict} - ${res.data.passed_tests}/${res.data.total_tests} tests passed`);
      }
    } catch (err) {
      console.error('Submission failed:', err);
      toast.error(err.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case 'easy': return 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30';
      case 'medium': return 'text-amber-400 bg-amber-500/20 border-amber-500/30';
      case 'hard': return 'text-rose-400 bg-rose-500/20 border-rose-500/30';
      default: return 'text-gray-400 bg-gray-500/20 border-gray-500/30';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader className="animate-spin text-cyan-400" size={40} />
      </div>
    );
  }

  if (!problem) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Code className="mx-auto text-gray-600 mb-4" size={48} />
          <p className="text-gray-400">Problem not found</p>
          <Link to="/codearena/problems" className="text-cyan-400 hover:underline mt-4 inline-block">
            Back to Problems
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex flex-col">
      {/* Header */}
      <div className="border-b border-white/10 bg-[#050505] px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link 
              to="/codearena/problems" 
              className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
              data-testid="back-btn"
            >
              <ArrowLeft size={18} />
              <span className="hidden sm:inline">Problems</span>
            </Link>
            <div className="h-6 w-px bg-white/10"></div>
            <div className="flex items-center gap-3">
              <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getDifficultyColor(problem.difficulty)}`}>
                {problem.difficulty?.toUpperCase()}
              </span>
              <h1 className="text-lg font-bold text-white truncate max-w-[300px]">
                {problem.title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-cyan-400 font-bold">+{problem.points} pts</span>
            {!isLoggedIn && (
              <Link
                to="/codearena/auth"
                className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg text-sm font-medium hover:bg-cyan-500/30 transition-colors"
                data-testid="login-btn"
              >
                Login to Submit
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Problem Description Panel */}
        <div className="lg:w-[45%] border-r border-white/10 overflow-y-auto">
          <div className="p-6">
            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-6">
              {problem.domains?.map((domain, idx) => (
                <span key={idx} className="text-xs text-cyan-400 bg-cyan-500/10 px-3 py-1.5 rounded-full border border-cyan-500/30">
                  {domain}
                </span>
              ))}
              {problem.tags?.map((tag, idx) => (
                <span key={idx} className="text-xs text-gray-400 bg-white/5 px-3 py-1.5 rounded-full border border-white/10">
                  {tag}
                </span>
              ))}
            </div>

            {/* Description */}
            <div className="prose prose-invert max-w-none">
              <h2 className="text-xl font-bold text-white mb-4">Problem Description</h2>
              <div className="text-gray-300 whitespace-pre-wrap leading-relaxed">
                {problem.description}
              </div>
            </div>

            {/* Sample I/O */}
            <div className="mt-8 space-y-4">
              <h3 className="text-lg font-semibold text-white">Example</h3>
              
              <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-white/5 border-b border-white/10">
                  <span className="text-sm font-medium text-gray-400">Input</span>
                </div>
                <pre className="p-4 text-sm text-gray-300 font-mono overflow-x-auto">
                  {problem.sample_input}
                </pre>
              </div>

              <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
                <div className="px-4 py-2 bg-white/5 border-b border-white/10">
                  <span className="text-sm font-medium text-gray-400">Output</span>
                </div>
                <pre className="p-4 text-sm text-gray-300 font-mono overflow-x-auto">
                  {problem.sample_output}
                </pre>
              </div>
            </div>

            {/* Constraints */}
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-white mb-4">Constraints</h3>
              <div className="flex gap-4">
                <div className="flex items-center gap-2 text-sm text-gray-400 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                  <Clock size={16} />
                  <span>Time: {problem.time_limit_seconds}s</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-400 bg-white/5 px-4 py-2 rounded-lg border border-white/10">
                  <Database size={16} />
                  <span>Memory: {problem.memory_limit_mb}MB</span>
                </div>
              </div>
            </div>

            {/* Hints */}
            {problem.hints && problem.hints.length > 0 && (
              <div className="mt-8">
                <button
                  onClick={() => setShowHints(!showHints)}
                  className="flex items-center gap-2 text-amber-400 hover:text-amber-300 transition-colors"
                  data-testid="toggle-hints"
                >
                  <Lightbulb size={18} />
                  <span>{showHints ? 'Hide Hints' : 'Show Hints'}</span>
                  {showHints ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                
                {showHints && (
                  <div className="mt-4 space-y-2">
                    {problem.hints.map((hint, idx) => (
                      <div key={idx} className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/20 rounded-lg p-4">
                        <span className="text-amber-400 font-bold">{idx + 1}.</span>
                        <span className="text-gray-300">{hint}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Submission Stats */}
            {problem.submission_stats && (
              <div className="mt-8 bg-white/5 border border-white/10 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-3">Statistics</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold text-white">{problem.submission_stats.total}</div>
                    <div className="text-xs text-gray-500">Submissions</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-400">{problem.submission_stats.accepted}</div>
                    <div className="text-xs text-gray-500">Accepted</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-cyan-400">{problem.submission_stats.acceptance_rate}%</div>
                    <div className="text-xs text-gray-500">Acceptance</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Code Editor Panel */}
        <div className="flex-1 flex flex-col min-h-[400px] lg:min-h-0">
          {/* Editor Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#050505]">
            <div className="flex items-center gap-3">
              <Code size={18} className="text-cyan-400" />
              <div className="relative">
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="appearance-none bg-white/5 border border-white/10 rounded-lg px-4 py-2 pr-8 text-white text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 cursor-pointer"
                  data-testid="language-select"
                >
                  <option value="python" className="bg-[#111]">Python 3</option>
                  <option value="javascript" className="bg-[#111]">JavaScript</option>
                  <option value="cpp" className="bg-[#111]">C++</option>
                  <option value="java" className="bg-[#111]">Java</option>
                </select>
                <ChevronDown size={16} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={submitting || !isLoggedIn}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="submit-btn"
            >
              {submitting ? (
                <>
                  <Loader className="animate-spin" size={18} />
                  Running...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Submit
                </>
              )}
            </button>
          </div>

          {/* Monaco Editor */}
          <div className="flex-1">
            <Editor
              height="100%"
              language={language}
              theme="vs-dark"
              value={code}
              onChange={(value) => setCode(value || '')}
              options={{
                fontSize: 14,
                minimap: { enabled: false },
                padding: { top: 16 },
                lineNumbers: 'on',
                scrollBeyondLastLine: false,
                wordWrap: 'on',
                automaticLayout: true,
                tabSize: 4
              }}
            />
          </div>

          {/* Result Panel */}
          {result && (
            <div className={`border-t border-white/10 p-4 ${
              result.verdict === 'Accepted' ? 'bg-emerald-500/10' : 'bg-rose-500/10'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  {result.verdict === 'Accepted' ? (
                    <CheckCircle className="text-emerald-400" size={24} />
                  ) : (
                    <XCircle className="text-rose-400" size={24} />
                  )}
                  <span className={`text-xl font-bold ${
                    result.verdict === 'Accepted' ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    {result.verdict}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-white">
                    {result.passed_tests}/{result.total_tests} Tests Passed
                  </div>
                  {result.first_solve && result.score_earned > 0 && (
                    <div className="text-cyan-400 font-semibold">+{result.score_earned} points earned!</div>
                  )}
                </div>
              </div>

              {/* Test Case Results */}
              <div className="grid grid-cols-3 gap-3">
                {result.test_results?.map((tc, idx) => (
                  <div 
                    key={idx}
                    className={`p-3 rounded-lg border ${
                      tc.passed 
                        ? 'bg-emerald-500/10 border-emerald-500/30' 
                        : 'bg-rose-500/10 border-rose-500/30'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {tc.passed ? (
                        <CheckCircle className="text-emerald-400" size={16} />
                      ) : (
                        <XCircle className="text-rose-400" size={16} />
                      )}
                      <span className={tc.passed ? 'text-emerald-400' : 'text-rose-400'}>
                        Test {idx + 1}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CodeArenaProblemSolvePage;
